<script>
  import TableDropdown from "components/Dropdowns/TableDropdown.svelte";
  import { fetchBancos, searchBancos, saveBanco, removeBanco, subscribeToBancos, unsubscribeFromBancos } from '../controllers/bancoController.js';
  import Swal from 'sweetalert2';
  import { onMount, onDestroy } from "svelte";
  import BancoPane from './BancoPane.svelte';
  import * as XLSX from 'xlsx';
  import { jsPDF } from "jspdf";
  import "jspdf-autotable";
  import BancoActions from './Actions.svelte';
  import Paginator from './Paginator.svelte'; // Import the Paginator


  let bancos = [];
  let totalRecords = 0;
  let currentPage = 1;
  let itemsPerPageOptions = [10, 20, 30, 40, 50, 100];
  let perPage = 10;
  let isPaneOpen = false;
  let newBanco = { descrip: '', sta: true };
  let currentBancoId = null;
  let isDirty = false;
  let searchQuery = '';
  let searchTimeout = null;
  let isSearching = false;
  let isMenuOpen = false; // Estado para controlar el menú hamburguesa

  function toggleMenu() {
    isMenuOpen = !isMenuOpen; // Cambia el estado del menú
  }

  async function handleSearch(page = 1) {
    if (searchQuery.trim() === '') {
      await fetchBancoData(page);
      isSearching = false;
    } else {
      const data = await searchBancos(searchQuery, page, perPage);
      updateBancosData(data, page);
      isSearching = true;
    }
  }

  function updateBancosData(data, page) {
    if (data?.bancos) {
      bancos = data.bancos;
      totalRecords = data.totalRecords || 0;
      currentPage = page;
    } else {
      resetBancoData();
    }
  }

  function resetBancoData() {
    bancos = [];
    totalRecords = 0;
    currentPage = 1;
  }

  function onSearchInput() {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => handleSearch(1), 300);
  }

  async function fetchBancoData(page = 1) {
    const data = await fetchBancos(page, perPage);
    updateBancosData(data, page);
  }

  function subscribeToBancoChanges() {
    subscribeToBancos(searchQuery, handleSearch);
  }

  function unsubscribeFromBancoChanges() {
    unsubscribeFromBancos();
  }

  onMount(() => {
    fetchBancoData();
    subscribeToBancoChanges();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      unsubscribeFromBancoChanges();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  });

  function handleBeforeUnload(event) {
    if (isDirty) {
      const confirmationMessage = 'Usted tiene cambios sin guardar. ¿Realmente desea salir?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
  }

  function togglePane() {
    isPaneOpen = !isPaneOpen;
    if (!isPaneOpen) resetForm();
  }

  function resetForm() {
    newBanco = { descrip: '', sta: true };
    currentBancoId = null;
    isDirty = false;
  }

  async function saveBancoEntry() {
    await saveBanco(newBanco, currentBancoId);
    await fetchBancoData(currentPage);
    togglePane();
    isDirty = false;
  }

  function editBanco(banco) {
    newBanco = { ...banco };
    currentBancoId = banco.id;
    isPaneOpen = true;
    isDirty = true;
  }

  function handleInputChange() {
    isDirty = true;
  }

  async function deleteBancoEntry(id) {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: "No podrás recuperar este banco después de eliminarlo.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminarlo!',
    });

    if (result.isConfirmed) {
      await removeBanco(id);
      await fetchBancoData(currentPage);
    }
  }

  async function handlePageChange(direction) {
    const newPage = currentPage + direction;
    const totalPages = Math.ceil(totalRecords / perPage);
    if (newPage > 0 && newPage <= totalPages) {
      isSearching 
        ? await handleSearch(newPage) 
        : await fetchBancoData(newPage);
    }
  }

  function exportToExcel() {
    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Prepare data for the worksheet
    const wsData = [
        ["Descripción", "Estado"], // Header
        ...bancos.map(banco => [banco.descrip, banco.sta ? 'Activo' : 'Inactivo'])
    ];

    // Create a worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Bancos");

    // Generate the Excel file and prompt the user to download
    XLSX.writeFile(wb, "bancos.xlsx");
  }

  function exportToPDF() {
    const doc = new jsPDF();

    // Define column headers
    const headers = [["Descripción", "Estado"]];

    // Define rows (data)
    const rows = bancos.map(banco => [
        banco.descrip,
        banco.sta ? 'Activo' : 'Inactivo'
    ]);

    // Use autoTable to create the table
    doc.autoTable({
        head: headers,
        body: rows,
        startY: 10, // Optional: where the table should start vertically
    });

    // Save the PDF
    doc.save("bancos.pdf");
}

function handleItemsPerPageChange(newPerPage) {
  perPage = newPerPage;
  currentPage = 1; // Reset to the first page when changing items per page
  fetchBancoData(currentPage); // Fetch the data for the new items per page
}
</script>
<div class="relative md:pt-6 pb-16 flex justify-between items-center">
  <div class="flex items-center mb-4">
    <h2 class="text-white text-lg font-bold">Gestión de Bancos</h2>
  </div>
  <div class="flex items-center ml-auto">
    <BancoActions 
      {togglePane}
      {exportToExcel}
      {exportToPDF}
      {isMenuOpen}
      {toggleMenu}
    />
    &nbsp;
    <input
      type="text"
      bind:value={searchQuery}
      on:input={onSearchInput}
      placeholder="Buscar ..."
      class="px-4 py-2 border rounded border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ml-4"
    />
  </div>
</div>

<BancoPane 
  {newBanco} 
  {currentBancoId} 
  {isPaneOpen} 
  {togglePane} 
  {saveBancoEntry} 
  on:inputChange={handleInputChange}
/>
<div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-white">
  <div class="rounded-t mb-0 px-4 py-3 border-0">
    <div class="flex flex-wrap items-center">
      <div class="relative w-full px-4 max-w-full flex-grow flex-1">
        <h3 class="font-semibold text-lg text-blueGray-700">Bancos</h3>
      </div>
    </div>
  </div>
  <div class="block w-full overflow-x-auto">
    <table class="items-center w-full bg-transparent border-collapse">
      <thead>
        <tr>
          <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Descripción</th>
          <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">Estado</th>
          <th class="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100"></th>
        </tr>
      </thead>
      <tbody>
        {#each bancos as banco}
          <tr>
            <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">{banco.descrip}</td>
            <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4">{banco.sta ? 'Activo' : 'Inactivo'}</td>
            <td class="border-t-0 px-6 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-4 text-right">
              <TableDropdown 
                onEdit={() => editBanco(banco)} 
                onDelete={() => deleteBancoEntry(banco.id)} 
                closeDropdown={() => {}} 
              />
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
</div>

<Paginator 
  {currentPage} 
  {totalRecords} 
  {perPage} 
  {itemsPerPageOptions} 
  onPageChange={handlePageChange} 
  onItemsPerPageChange={handleItemsPerPageChange}
/>